import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCreditCard, FiLock, FiCheck, FiX, FiAlertCircle, FiLoader } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyPOS Payment Button Component
 * Creates a payment session and redirects to MyPOS checkout
 */
export const MyPOSPaymentButton = ({
  reservationId,
  amount,
  currency = 'EUR',
  description = 'Acompte réservation',
  customerEmail,
  customerFirstName,
  customerLastName,
  customerPhone,
  onSuccess,
  onError,
  className = '',
  disabled = false,
  children
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initiatePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/payments/mypos/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservation_id: reservationId,
          amount,
          currency,
          description,
          customer_email: customerEmail,
          customer_first_name: customerFirstName,
          customer_last_name: customerLastName,
          customer_phone: customerPhone,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la création du paiement');
      }

      const data = await response.json();

      // Create and submit form to MyPOS
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.checkout_url;
      form.target = '_self';

      Object.entries(data.form_data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();

      if (onSuccess) onSuccess(data);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={initiatePayment}
        disabled={disabled || loading}
        className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
          disabled || loading
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl'
        } ${className}`}
      >
        {loading ? (
          <>
            <FiLoader className="h-5 w-5 animate-spin" />
            Chargement...
          </>
        ) : (
          <>
            <FiCreditCard className="h-5 w-5" />
            {children || `Payer ${amount.toFixed(2)} ${currency}`}
          </>
        )}
      </button>
      {error && (
        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
          <FiAlertCircle className="h-4 w-4" />
          {error}
        </p>
      )}
    </>
  );
};

/**
 * Payment Status Badge Component
 */
export const PaymentStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Payé' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Échoué' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Annulé' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

/**
 * Payment Modal Component
 * Shows a modal for payment with deposit breakdown
 */
export const PaymentModal = ({
  isOpen,
  onClose,
  reservationId,
  totalAmount,
  depositPercentage = 30,
  currency = 'EUR',
  customerEmail,
  customerFirstName,
  customerLastName,
  customerPhone,
  serviceName,
  eventDate,
  primaryColor = '#d4af37'
}) => {
  const depositAmount = (totalAmount * depositPercentage) / 100;
  const remainingAmount = totalAmount - depositAmount;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl max-w-md w-full p-8 relative"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX className="h-5 w-5" />
          </button>

          <div className="text-center mb-6">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <FiLock className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-2xl font-bold mb-2">Paiement de l'acompte</h3>
            <p className="text-gray-500 text-sm">
              Sécurisez votre réservation avec le paiement de l'acompte
            </p>
          </div>

          {/* Reservation Summary */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h4 className="font-semibold mb-3">Récapitulatif</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Prestation</span>
                <span className="font-medium">{serviceName}</span>
              </div>
              {eventDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium">{eventDate}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Montant total</span>
                <span className="font-medium">{totalAmount.toFixed(2)} {currency}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Acompte ({depositPercentage}%)</span>
                  <span className="text-lg font-bold" style={{ color: primaryColor }}>
                    {depositAmount.toFixed(2)} {currency}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Reste à payer le jour J</span>
                  <span>{remainingAmount.toFixed(2)} {currency}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg mb-6">
            <FiLock className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-emerald-700">
              Paiement sécurisé par <strong>MyPOS</strong>. Vos données bancaires ne sont jamais stockées sur notre serveur.
            </p>
          </div>

          {/* Payment Button */}
          <MyPOSPaymentButton
            reservationId={reservationId}
            amount={depositAmount}
            currency={currency}
            description={`Acompte - ${serviceName}`}
            customerEmail={customerEmail}
            customerFirstName={customerFirstName}
            customerLastName={customerLastName}
            customerPhone={customerPhone}
            className="w-full py-4 text-lg"
          >
            <FiCreditCard className="h-5 w-5 mr-2" />
            Payer {depositAmount.toFixed(2)} {currency}
          </MyPOSPaymentButton>

          {/* Payment Methods */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400 mb-2">Moyens de paiement acceptés</p>
            <div className="flex justify-center gap-2">
              {['VISA', 'MasterCard', 'CB'].map(method => (
                <span
                  key={method}
                  className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600"
                >
                  {method}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Payment Success Page Component
 */
export const PaymentSuccessPage = ({ orderId, onContinue }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white p-6">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-24 h-24 bg-emerald-100 rounded-full mx-auto mb-6 flex items-center justify-center"
        >
          <FiCheck className="h-12 w-12 text-emerald-600" />
        </motion.div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Paiement réussi !
        </h1>
        <p className="text-gray-600 mb-6">
          Votre acompte a été reçu avec succès. Vous recevrez un email de confirmation sous peu.
        </p>

        {orderId && (
          <p className="text-sm text-gray-500 mb-6">
            Référence: <span className="font-mono font-semibold">{orderId}</span>
          </p>
        )}

        <button
          onClick={onContinue}
          className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </motion.div>
    </div>
  );
};

/**
 * Payment Cancelled Page Component
 */
export const PaymentCancelledPage = ({ onRetry, onGoBack }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-6">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-md w-full text-center"
      >
        <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
          <FiX className="h-12 w-12 text-gray-500" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Paiement annulé
        </h1>
        <p className="text-gray-600 mb-6">
          Votre paiement a été annulé. Aucun montant n'a été débité de votre compte.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
            >
              Réessayer
            </button>
          )}
          <button
            onClick={onGoBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
          >
            Retour
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default {
  MyPOSPaymentButton,
  PaymentStatusBadge,
  PaymentModal,
  PaymentSuccessPage,
  PaymentCancelledPage
};
